<template>
  <div class="reserve-dining-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('reserveDiningManage.listTitle')" :subtitle="$t('reserveDiningManage.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：目录树 -->
      <el-col :xs="24" :md="6" :lg="5">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('reserveDiningManage.addCatalog') }}</span>
          </div>
          <ul v-if="reserveDiningManageInfo.catalogs && reserveDiningManageInfo.catalogs.length > 0" class="ds-tree">
            <li v-for="(item, index) in reserveDiningManageInfo.catalogs" :key="index" class="ds-tree__item"
              :class="{ 'ds-tree__item--active': reserveDiningManageInfo.conditions.catalogId == item.catalogId }"
              @click="swatchReserveCatalog(item)">
              {{ item.name }}
            </li>
          </ul>
          <el-empty v-else :description="$t('reserveDiningManage.page.noCatalog')" :image-size="60" />
          <el-button type="text" icon="el-icon-plus" class="ds-tree__add" @click="_addCatalog">
            {{ $t('reserveDiningManage.addCatalog') }}
          </el-button>
        </div>
      </el-col>

      <!-- 右侧：查询 + 列表 -->
      <el-col :xs="24" :md="18" :lg="19">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="reserveDiningManageInfo.conditions.goodsId" clearable
                :placeholder="$t('reserveDiningManage.goodsIdPlaceholder')"
                @keyup.enter.native="_queryReserveDiningMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="reserveDiningManageInfo.conditions.goodsName" clearable
                :placeholder="$t('reserveDiningManage.goodsNamePlaceholder')"
                @keyup.enter.native="_queryReserveDiningMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="reserveDiningManageInfo.conditions.state" clearable class="w-full"
                :placeholder="$t('reserveDiningManage.statePlaceholder')">
                <el-option :label="$t('reserveDiningManage.stateNotOnline')" value="1001" />
                <el-option :label="$t('reserveDiningManage.stateOnline')" value="2002" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryReserveDiningMethod">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetReserveDiningMethod">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="_queryReserveDiningMethod">
            <template #left>
              <span class="ds-panel__title">{{ $t('reserveDiningManage.listTitle') }}</span>
            </template>
            <template #right>
              <el-button type="primary" size="mini" icon="el-icon-data-analysis" @click="_doOwnerDining">
                {{ $t('reserveDiningManage.diningStatistics') }}
              </el-button>
              <el-button type="primary" size="mini" icon="el-icon-shopping-cart-2" @click="_doDining">
                {{ $t('reserveDiningManage.dining') }}
              </el-button>
              <el-button type="primary" size="mini" icon="el-icon-plus" @click="_openAddReserveDiningModal">
                {{ $t('common.add') }}
              </el-button>
            </template>
          </ds-toolbar>

          <ds-data-table :data="reserveDiningManageInfo.reserveDinings" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('reserveDiningManage.page.empty')" @retry="_queryReserveDiningMethod">
            <el-table-column :label="$t('reserveDiningManage.goodsCover')" align="center" width="90">
              <template slot-scope="scope">
                <el-image class="ds-cover" :src="scope.row.imgUrl || '/img/noPhoto.jpg'" fit="cover" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveDiningManage.goodsName')" align="left" min-width="180" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.goodsName }}<span class="ds-num">({{ scope.row.goodsId }})</span>
              </template>
            </el-table-column>
            <el-table-column prop="paramsName" :label="$t('reserveDiningManage.params')" align="center" min-width="120" show-overflow-tooltip />
            <el-table-column :label="$t('reserveDiningManage.reserveWay')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :label="scope.row.paramWay == '1' ? $t('reserveDiningManage.day') : $t('reserveDiningManage.week')"
                  :text="scope.row.paramWay == '1' ? $t('reserveDiningManage.day') : $t('reserveDiningManage.week')" type="info" />
              </template>
            </el-table-column>
            <el-table-column prop="maxQuantity" :label="$t('reserveDiningManage.reserveCount')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.maxQuantity }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="hoursMaxQuantity" :label="$t('reserveDiningManage.reserveQuantity')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.hoursMaxQuantity }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="price" :label="$t('reserveDiningManage.price')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.price }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveDiningManage.validDate')" align="center" width="200">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.startDate }}</span><br /><span class="ds-num">~{{ scope.row.endDate }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="startTime" :label="$t('reserveDiningManage.reserveStartTime')" align="center" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="sort" :label="$t('reserveDiningManage.sort')" align="center" width="90">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.sort }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveDiningManage.state')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :label="scope.row.state == '1001' ? $t('reserveDiningManage.stateNotOnline') : $t('reserveDiningManage.stateOnline')"
                  :text="scope.row.state == '1001' ? $t('reserveDiningManage.stateNotOnline') : $t('reserveDiningManage.stateOnline')"
                  :type="scope.row.state == '1001' ? 'info' : 'success'" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="230" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="_openReserveDiningPersonModel(scope.row)">
                  {{ $t('reserveDiningManage.reserveDining') }}
                </el-button>
                <el-button size="mini" type="text" @click="_openEditReserveDiningModel(scope.row)">
                  {{ $t('common.edit') }}
                </el-button>
                <el-button size="mini" type="text" class="ds-btn-danger" @click="_openDeleteReserveDiningModel(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <add-reserve-dining-person ref="addReserveDiningPerson" @success="handleSuccess" />
    <delete-reserve-dining ref="deleteReserveDining" @success="handleSuccess" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { listReserveGoods, listReserveCatalog } from '@/api/scm/reserveDiningManageApi'
import AddReserveDiningPerson from '@/components/scm/addReserveDiningPerson'
import DeleteReserveDining from '@/components/scm/deleteReserveDining'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ReserveDiningManageList',
  components: {
    AddReserveDiningPerson,
    DeleteReserveDining,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      reserveDiningManageInfo: {
        reserveDinings: [],
        catalogs: [],
        conditions: {
          goodsId: '',
          goodsName: '',
          type: '1001',
          state: '',
          catalogId: '',
          communityId: getCommunityId(),
          page: 1,
          row: 10
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this._listReserveCatalogs()
  },
  methods: {
    async _listReserveDinings(page, rows) {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        this.reserveDiningManageInfo.conditions.page = page || this.page.current
        this.reserveDiningManageInfo.conditions.row = rows || this.page.size

        const { data, total } = await listReserveGoods(this.reserveDiningManageInfo.conditions)
        this.reserveDiningManageInfo.reserveDinings = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error(error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('reserveDiningManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    async _listReserveCatalogs() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: getCommunityId(),
          type: '1001'
        }

        const { data } = await listReserveCatalog(params)
        this.reserveDiningManageInfo.catalogs = Array.isArray(data) ? data : []
        if (data && data.length > 0) {
          this.swatchReserveCatalog(data[0])
        }
      } catch (error) {
        console.error(error)
      }
    },
    swatchReserveCatalog(item) {
      this.reserveDiningManageInfo.conditions.catalogId = item.catalogId
      this._listReserveDinings()
    },
    _queryReserveDiningMethod() {
      this.page.current = 1
      this._listReserveDinings()
    },
    _resetReserveDiningMethod() {
      this.reserveDiningManageInfo.conditions.goodsId = ''
      this.reserveDiningManageInfo.conditions.goodsName = ''
      this.reserveDiningManageInfo.conditions.state = ''
      this.page.current = 1
      this._listReserveDinings()
    },
    _openAddReserveDiningModal() {
      if (!this.reserveDiningManageInfo.conditions.catalogId) {
        this.$message.warning(this.$t('reserveDiningManage.addCatalogFirst'))
        return
      }
      this.$router.push(`/views/scm/addReserveDining?catalogId=${this.reserveDiningManageInfo.conditions.catalogId}`)
    },
    _openEditReserveDiningModel(reserveDining) {
      this.$router.push(`/views/scm/editReserveDining?goodsId=${reserveDining.goodsId}`)
    },
    _openDeleteReserveDiningModel(reserveDining) {
      this.$refs.deleteReserveDining.open(reserveDining)
    },
    _openReserveDiningPersonModel(reserveDining) {
      this.$refs.addReserveDiningPerson.open(reserveDining)
    },
    _addCatalog() {
      this.$router.push('/views/scm/reserveCatalogManage?tab=reserveCatalog')
    },
    _doDining() {
      if (!this.reserveDiningManageInfo.reserveDinings || this.reserveDiningManageInfo.reserveDinings.length < 1) {
        this.$message.warning(this.$t('reserveDiningManage.addFirst'))
        return
      }
      this.$router.push('/views/scm/doDining')
    },
    _doOwnerDining() {
      this.$router.push('/views/scm/ownerDining')
    },
    handleSuccess() {
      this._listReserveDinings()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listReserveDinings()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listReserveDinings()
    }
  }
}
</script>

<style lang="scss" scoped>
.reserve-dining-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-tree {
  list-style: none;
  padding: 0;
  margin: 0;

  &__item {
    padding: 9px 12px;
    border: 1px solid var(--ds-gray-300);
    margin-bottom: -1px;
    cursor: pointer;
    font-size: var(--ds-font-size-md);
    color: var(--ds-gray-700);
    transition: background-color 0.2s, color 0.2s;

    &:first-child {
      border-radius: var(--ds-radius-sm) var(--ds-radius-sm) 0 0;
    }

    &:last-child {
      border-radius: 0 0 var(--ds-radius-sm) var(--ds-radius-sm);
    }

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &--active {
      background-color: var(--ds-primary);
      color: #fff;
      border-color: var(--ds-primary);
    }
  }

  &__add {
    margin-top: var(--ds-spacing-sm);
    padding: 0;
  }
}

.ds-cover {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
